import { ChronoUnit, LocalDate } from "@js-joda/core";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";

/**
 * Formats a personal identity number for display.
 * Accepts `YYYYMMDDXXXX` and `YYYYMMDD-XXXX`; returns "-" when empty/invalid.
 */
export const formatPersonalIdentityNumber = (
  personalIdentityNumber: string | null,
): string => {
  const normalizedValue = (personalIdentityNumber ?? "").trim();
  if (normalizedValue.length === 0) return "-";
  if (/^\d{8}-\d{4}$/.test(normalizedValue)) return normalizedValue;
  if (/^\d{12}$/.test(normalizedValue)) {
    return `${normalizedValue.slice(0, 8)}-${normalizedValue.slice(8, 12)}`;
  }
  return "-";
};

/**
 * Derives the worker's age in whole years from a Swedish personal identity number.
 * Reads the birth date from the leading `YYYYMMDD`; returns null when empty/invalid.
 */
export const getAgeFromPersonalIdentityNumber = (
  personalIdentityNumber: string | null,
  today: LocalDate = getStockholmLocalDateNow(),
): number | null => {
  const digits = (personalIdentityNumber ?? "").replace(/\D/g, "");
  if (digits.length < 8) return null;

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));

  let birthDate: LocalDate;
  try {
    birthDate = LocalDate.of(year, month, day);
  } catch {
    return null;
  }

  if (birthDate.isAfter(today)) return null;
  return birthDate.until(today, ChronoUnit.YEARS);
};
